import { useCallback } from "react";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "components/UI/Tooltip";
import { useCesiumContext } from "stores/useCesiumContext";
import { useEditMode } from "stores/useEditMode";
import { useMapContext } from "stores/useMapContext";
import { useUserWaypoints } from "stores/useUserWaypoints";
import { useTranslation } from "react-i18next";
import { useRouting } from "stores/routingStore/RoutingContext";
import { fitTrackToScreen, fitWaypointToScreen } from "utils/map/fitToScreen";
import { CollapsibleTopMenuActionIcon } from "../CollapsibleActionIcon";

export const FitToScreenIcon = ({ isOpen }: { isOpen: boolean }) => {
  const { map } = useMapContext();
  const {
    state: { computedTrackpoints, trackWaypoints },
  } = useRouting();
  const { editMode } = useEditMode();
  const { selectedWaypoint } = useUserWaypoints();
  const { t } = useTranslation();
  const { cesium } = useCesiumContext();

  const fitToScreen = useCallback(() => {
    if (map) {
      if (editMode === "track") {
        if (trackWaypoints.length === 1) {
          map.setView({
            lat: trackWaypoints[0][0],
            lng: trackWaypoints[0][1],
          });
        } else {
          fitTrackToScreen(computedTrackpoints, map);
        }
      }
      if (editMode === "waypoint" && selectedWaypoint) {
        fitWaypointToScreen(selectedWaypoint.point, map);
      }
    } else if (cesium) {
      // Cesium is loaded lazily together with the 3D map, keep it out of the main bundle
      void import("utils/cesium/flyToCoords").then(({ flyToCoords }) =>
        flyToCoords(cesium, computedTrackpoints)
      );
    }
  }, [
    cesium,
    computedTrackpoints,
    editMode,
    map,
    selectedWaypoint,
    trackWaypoints,
  ]);

  const label = selectedWaypoint
    ? t("editmode.fit_to_screen_button.fit_waypoint_to_view_tooltip")
    : t("editmode.fit_to_screen_button.fit_track_to_view_tooltip");

  return (
    <Tooltip label={label}>
      <div>
        <CollapsibleTopMenuActionIcon
          isOpen={isOpen}
          label={label}
          onClick={fitToScreen}
        >
          <FontAwesomeIcon icon={faExpand} />
        </CollapsibleTopMenuActionIcon>
      </div>
    </Tooltip>
  );
};
